import { useNavigate } from 'react-router-dom';

import { AppIcon, EducationalInvestment } from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { SEARCH_PAGE_CATEGORIES } from '@backrs_ui/grow-ui/src/pages/Search/search.types';

import { Box, Button, Chip, SxProps, Theme, Typography } from '@mui/material';

const STRINGS = appStrings.modules.survey.output.blueprintSection.educationalInvestments.detail;

type EducationalInvestmentDetailProps = Readonly<{
  educationalInvestment?: { target: EducationalInvestment[] };
}>;

export function EducationalInvestmentDetail({
  educationalInvestment,
}: EducationalInvestmentDetailProps) {
  const navigate = useNavigate();

  if (!educationalInvestment) return null;

  const { target } = educationalInvestment;

  return (
    <Box sx={containerSx}>
      {/* Header: title + nav CTA */}
      <Box sx={headerSx}>
        <Typography variant="display-xs-semibold">{STRINGS.heading}</Typography>
        <Button
          id="education-detail-explore-btn"
          hierarchy="Secondary gray"
          size="md"
          onClick={() => navigate(`${APP_ROUTE.SEARCH}/${SEARCH_PAGE_CATEGORIES.COLLEGES}`)}
        >
          {STRINGS.exploreCta}
        </Button>
      </Box>

      {/* Investment cards grid */}
      <Box sx={gridSx}>
        {target.map((inv) => (
          <Box key={inv.id} sx={cardSx} id={`education-detail-card-${inv.id}`}>
            <Chip
              variant="badge"
              size="md"
              type="leading"
              label={inv.name}
              icon={
                <AppIcon
                  iconName={inv.icon || 'GraduationHat02'}
                  diameter="16px"
                  color="Brand/600"
                />
              }
              sx={badgeSx}
            />
            <Typography variant="text-sm-regular" sx={descriptionSx}>
              {inv.description}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// --- Styles ---

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  gap: backrs.spacing['spacing-3xl'],
});

const headerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: backrs.spacing['spacing-xl'],
  flexWrap: 'wrap',
});

const gridSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gap: backrs.spacing['spacing-xl'],
  gridTemplateColumns: 'minmax(0, 1fr)',
  '@container (min-width: 500px)': {
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  },
});

const cardSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
  padding: backrs.spacing['spacing-xl'],
  borderRadius: backrs.radius['radius-xl'],
  border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  backgroundColor: backrs.colors['Colors/Base/White'],
});

const badgeSx: SxProps<Theme> = ({ backrs }) => ({
  alignSelf: 'flex-start',
  backgroundColor: backrs.colors['Brand/50'],
  border: `1px solid ${backrs.colors['Brand/200']}`,
  '& .MuiChip-label': {
    color: backrs.colors['Brand/700'],
  },
});

const descriptionSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});
